import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../environments/environment';

/**
 * Value types GA4 accepts for an event parameter. Nested objects and arrays
 * are dropped silently by GA4, so the type forbids them rather than letting
 * a call site discover it a month later in an empty report.
 */
export type AnalyticsValue = string | number | boolean;

/**
 * The event catalogue.
 *
 * Keys are GA4 event names and must always be literals at the call site --
 * never built by concatenation. GA4 caps a property at 500 distinct event
 * names and silently drops everything past that, and a constructed name
 * cannot be grepped for. The original implementation built names as
 * "Button click: " + info, which is exactly the failure this map prevents.
 *
 * `login` is a GA4 recommended name and feeds Google's built-in reports.
 * The rest are custom events. Their custom parameters (label, form,
 * error_key) do not appear anywhere in the GA4 UI until they are registered
 * as custom dimensions in Admin -- see docs/analytics.md.
 */
export interface AnalyticsEventMap {
    /** Buyer or seller completed sign-in. `method` is the GA4 convention. */
    login: { method: 'buyer' | 'seller' };
    /** A validation or server error surfaced on a form. */
    form_error: { form: string; error_key: string };
    /** A named button was pressed. */
    button_click: { label: string };
    /** An in-page section or dialog was opened (not a route change). */
    open_view: { label: string };
    /** A non-form error worth counting. */
    app_error: { label: string };
}

export type AnalyticsEventName = keyof AnalyticsEventMap;

/**
 * Where the user is, as GA4 names it. Built by resolvePageContext() in
 * analytics.providers.ts, which is the only thing allowed to construct one:
 * the values are scrubbed of credentials and record ids there, and building
 * one by hand elsewhere would defeat that.
 */
export interface PageContext {
    page_location: string;
    page_path: string;
    page_title: string;
}

declare const gtag: ((...args: unknown[]) => void) | undefined;

/** GA4 truncates event parameter values at 100 characters. */
const MAX_PARAM_LENGTH = 100;

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
    private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    /**
     * Send one catalogued event to every configured sink.
     *
     * Never throws and never blocks. Analytics is fire-and-forget, and a
     * failure here must not reach the caller: r12198 is the cautionary tale,
     * where an unguarded gtag call in ngOnInit threw during server render and
     * took the whole page down with it.
     *
     * Do not pass account numbers, addresses, emails or names in props. GA4's
     * terms prohibit PII and Google disables properties that send it. Note
     * that server error text is not safe to forward either --
     * ApiUsersController returns a utility account number in one BadRequest
     * body and a raw exception message in another, which is why form_error
     * takes a static error_key rather than the server's string.
     */
    trackEvent<K extends AnalyticsEventName>(event: K, props: AnalyticsEventMap[K]): void {
        this.callGtag('event', event, this.normalize(props as Record<string, AnalyticsValue | undefined>));
    }

    /**
     * Page view. Deliberately not routed through trackEvent(): GA4 reserves
     * these parameter names and they must reach gtag unmodified.
     *
     * Called from the router subscription in analytics.providers.ts, never
     * from a component -- ngOnInit fires per component rather than per
     * navigation, and runs during server render where gtag does not exist.
     */
    trackPageView(context: PageContext): void {
        this.setPageContext(context);
        this.callGtag('event', 'page_view', { ...context });
    }

    /**
     * Make this page the default for every event that follows.
     *
     * Not an optimisation -- a containment measure. gtag remembers the
     * page_location it was configured with and stamps it on every subsequent
     * event, so a form_error fired on /user/home?auth=<token> would carry the
     * token even though page_view itself was clean. Overwriting the default on
     * each navigation is what keeps the scrub in resolvePageContext() from
     * being trivially bypassed.
     */
    setPageContext(context: PageContext): void {
        this.callGtag('set', { ...context });
    }

    private callGtag(...args: unknown[]): void {
        if (!this.isBrowser) { return; }

        // Empty measurement ID means this tier does not report (test, local).
        // Log locally so a developer can still see what would have been sent.
        if (!environment.GA4_MEASUREMENT_ID) {
            if (!environment.production) {
                console.debug('[analytics] not sent, no measurement ID:', ...args);
            }
            return;
        }

        try {
            if (typeof gtag !== 'undefined') {
                gtag(...args);
            }
        } catch (error) {
            // Swallowed on purpose -- see the note on trackEvent().
            if (!environment.production) {
                console.warn('[analytics] send failed:', error);
            }
        }
    }

    /**
     * Drops empty values and truncates to GA4's parameter limit, so that no
     * call site has to remember either rule.
     */
    private normalize(props: Record<string, AnalyticsValue | undefined>): Record<string, AnalyticsValue> {
        const normalized: Record<string, AnalyticsValue> = {};

        for (const [key, value] of Object.entries(props)) {
            if (value === undefined || value === null || value === '') { continue; }
            normalized[key] = typeof value === 'string' ? value.slice(0, MAX_PARAM_LENGTH) : value;
        }

        return normalized;
    }
}
